import { Fragment, useMemo, type FunctionComponent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  NavigationBar,
  TOPBAR_HEIGHT,
  SIDEBAR_WIDTH,
} from '@retailer-platform/shared-components/src/tds'
import { useCoreContext } from '../../../utils/core/RPPCoreContext'
import { useAbsoluteRouteMatch } from '../../../utils/routing/useAbsoluteRouteMatch.hooks'
import { url as resolveUrlPattern } from '../../../utils/parsing/url'
import icPlatformLogo from '../nav-v2/assets/ic-platform.svg'
import { type NavMenuHierarchy, type NavMenuHierarchyL2 } from '../nav-v2/NavMenuHierarchy'
import { TdsHelpMenu } from './TdsHelpMenu'
import { TdsNotificationsButton } from './TdsNotificationsButton'
import { TdsPartnerMenu } from './TdsPartnerMenu'
import { TdsSearchButton } from './TdsSearchButton'
import { TdsUserMenu } from './TdsUserMenu'
import { TopBarMenuProvider } from './TopBarMenuContext'

type Props = {
  navMenuHierarchy: NavMenuHierarchy
}

type ResolvedNavHref = { href: string; isExternal: boolean } | undefined

const useResolveNavHref = (l2: NavMenuHierarchyL2): ResolvedNavHref => {
  const { routesByName } = useCoreContext()
  const routeMatch = useAbsoluteRouteMatch()

  return useMemo(() => {
    if (l2.href) return { href: l2.href, isExternal: true }
    if (!l2.route) return undefined

    const pattern = routesByName[l2.route]
    if (!pattern) return undefined

    return {
      href: resolveUrlPattern(pattern, (routeMatch?.params as Record<string, string>) ?? {}),
      isExternal: Boolean(l2.newTab),
    }
  }, [l2.href, l2.route, l2.newTab, routesByName, routeMatch?.params])
}

const TdsNavLeaf: FunctionComponent<{ l2: NavMenuHierarchyL2 }> = ({ l2 }) => {
  const resolved = useResolveNavHref(l2)
  return (
    <NavigationBar.NavItem href={resolved?.href} isExternal={resolved?.isExternal ?? false}>
      <FormattedMessage id={l2.name} />
    </NavigationBar.NavItem>
  )
}

export const TdsNavigationBar: FunctionComponent<Props> = ({ navMenuHierarchy }) => {
  const intl = useIntl()
  return (
    <NavigationBar>
      <NavigationBar.TopBar>
        <NavigationBar.TopBarLeft>
          <img
            src={icPlatformLogo}
            alt="Instacart Platform"
            style={{ height: 18, marginLeft: 16, display: 'block' }}
          />
        </NavigationBar.TopBarLeft>
        <NavigationBar.TopBarRight>
          <TopBarMenuProvider>
            <TdsNotificationsButton />
            <TdsSearchButton />
            <TdsHelpMenu />
            <TdsUserMenu />
            <TdsPartnerMenu />
          </TopBarMenuProvider>
        </NavigationBar.TopBarRight>
      </NavigationBar.TopBar>
      <NavigationBar.Sidebar defaultCollapsed={false}>
        <NavigationBar.SidebarContent aria-label="Primary navigation">
          {navMenuHierarchy.hierarchy.map((l1, i) => {
            const L1Icon = l1.icon
            return (
              <NavigationBar.NavGroup key={`${l1.name}-${i}`}>
                <NavigationBar.NavGroupTrigger icon={<L1Icon size={20} />}>
                  <FormattedMessage id={l1.name} />
                </NavigationBar.NavGroupTrigger>
                <NavigationBar.NavGroupContent>
                  {l1.hasL2Children
                    ? l1.children.map((l2, j) => (
                        <Fragment key={`${l2.name}-${j}`}>
                          <NavigationBar.NavSection title={intl.formatMessage({ id: l2.name })} />
                          {(l2.children ?? []).map((l3, k) => (
                            <TdsNavLeaf key={`${l3.name}-${k}`} l2={l3} />
                          ))}
                        </Fragment>
                      ))
                    : l1.children.map((l2, j) => <TdsNavLeaf key={`${l2.name}-${j}`} l2={l2} />)}
                </NavigationBar.NavGroupContent>
              </NavigationBar.NavGroup>
            )
          })}
        </NavigationBar.SidebarContent>
      </NavigationBar.Sidebar>
    </NavigationBar>
  )
}

// Re-exported so consumers building layout around the NavigationBar (e.g. sizing the
// content area to the right of the Sidebar and below the TopBar) can use the canonical
// TDS constants instead of hardcoding them.
export { TOPBAR_HEIGHT, SIDEBAR_WIDTH }
